// MethodSection.js - Version avec animations raffinées
import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  MdSearch, 
  MdDesignServices, 
  MdRocketLaunch, 
  MdTrendingUp,
  MdCheckCircle,
  MdArrowForward,
  MdTimer,
  MdPeople,
  MdAnalytics,
  MdAutoAwesome,
  MdFlashOn,
  MdVerified,
  MdInsights,
  MdCloudQueue,
  MdSpeed,
  MdWorkspacePremium
} from 'react-icons/md';
import { 
  FaChartLine, 
  FaRocket, 
  FaCrown, 
  FaGem, 
  FaMagic,
  FaBrain,
  FaPallet,
  FaInfinity
} from 'react-icons/fa';
import { GiAstronautHelmet, GiGalaxy, GiStarsStack, GiCrystalBall, GiSpiralShell } from 'react-icons/gi';
import './MethodSection.css';

const MethodSection = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.8, 1, 1, 0.8]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Auto-advance steps for demo
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev % 4) + 1);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      id: 1,
      number: "01",
      icon: <MdSearch />,
      iconAlt: <MdInsights />,
      title: "Diagnostic",
      description: "On cartographie vos processus existants et on identifie précisément où vous perdez du temps et de l'argent.",
      details: [
        "Analyse approfondie de vos processus",
        "Identification des goulots d'étranglement",
        "Calcul du temps et argent perdus",
        "Recommandations personnalisées"
      ],
      duration: "30 min",
      price: "Gratuit",
      commitment: "Sans engagement",
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.4)",
      stats: ["+150% ROI", "0 risque"]
    },
    {
      id: 2,
      number: "02",
      icon: <MdDesignServices />,
      iconAlt: <MdCloudQueue />,
      title: "Conception",
      description: "On conçoit les workflows et systèmes sur mesure, adaptés à votre équipe, vos outils, votre façon de travailler.",
      details: [
        "Workflows personnalisés",
        "Intégration avec vos outils existants",
        "Architecture adaptée à votre équipe",
        "Documentation claire et simple"
      ],
      duration: "Sur mesure",
      price: "Adapté à vos besoins",
      commitment: "Aucun jargon tech",
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.4)",
      stats: ["+200% efficacité", "100% personnalisé"]
    },
    {
      id: 3,
      number: "03",
      icon: <MdRocketLaunch />,
      iconAlt: <MdSpeed />,
      title: "Déploiement",
      description: "On installe, configure et forme votre équipe. Vous êtes autonomes rapidement, sans dépendance technique.",
      details: [
        "Installation et configuration complète",
        "Formation de votre équipe",
        "Support pendant le déploiement",
        "Documentation et guides vidéo"
      ],
      duration: "Accompagnement inclus",
      price: "Inclus",
      commitment: "Autonomie garantie",
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.4)",
      stats: ["-80% erreurs", "Autonomie totale"]
    },
    {
      id: 4,
      number: "04",
      icon: <MdTrendingUp />,
      iconAlt: <FaCrown />,
      title: "Résultat",
      description: "Votre équipe gagne du temps, les erreurs diminuent, votre business avance sans tout reposer sur vous.",
      details: [
        "Gain de temps immédiat",
        "Réduction des erreurs",
        "Processus automatisés",
        "Scalabilité assurée"
      ],
      duration: "Visible dès la 1ère semaine",
      price: "ROI mesurable",
      commitment: "Transformation durable",
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.4)",
      stats: ["+300% productivité", "Croissance assurée"]
    }
  ];

  return (
    <section className="ms-section-root" ref={sectionRef}>
      {/* Interactive Cursor Glow - Version améliorée */}
      <motion.div 
        className="ms-cursor-glow"
        animate={{
          x: mousePosition.x - 200,
          y: mousePosition.y - 200,
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 200,
          mass: 0.5
        }}
      />

      {/* Animated Background - Plus sophistiqué */}
      <div className="ms-bg-gradient-animation"></div>
      
      {/* Grille d'arrière-plan élégante */}
      <div className="ms-grid-pattern"></div>
      
      {/* Particules flottantes optimisées */}
      <div className="ms-particles-container">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="ms-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 20}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              opacity: 0.15 + Math.random() * 0.35
            }}
          />
        ))}
      </div>

      {/* Cercles lumineux animés */}
      <div className="ms-light-orbs">
        <div className="ms-orb ms-orb-1" />
        <div className="ms-orb ms-orb-2" />
        <div className="ms-orb ms-orb-3" />
      </div>

      {/* Éléments flottants minimalistes */}
      <div className="ms-floating-elements">
        <GiGalaxy className="ms-floating ms-floating-1" />
        <GiSpiralShell className="ms-floating ms-floating-2" />
        <GiStarsStack className="ms-floating ms-floating-3" />
      </div>

      <div className="ms-container">
        {/* Section Header - Design plus épuré */}
        <motion.div 
          className="ms-header"
          style={{ opacity, scale }}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <motion.div 
            className="ms-badge-wrapper"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            <div className="ms-badge">
              <GiSpiralShell className="ms-badge-icon" />
              <span>Notre Méthodologie</span>
              <MdAutoAwesome className="ms-badge-icon" />
            </div>
          </motion.div>
          
          <motion.h2 
            className="ms-title"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15, type: "spring", stiffness: 200 }}
          >
            <span className="ms-title-line">
              Comment nous <span className="ms-gradient-text">transformons</span>
            </span>
            <span className="ms-title-line">
              votre <span className="ms-title-highlight">business</span>
            </span>
            <div className="ms-title-underline"></div>
          </motion.h2>
          
          <motion.p 
            className="ms-subtitle"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Une approche simple et structurée en 4 étapes pour des résultats concrets et durables.
          </motion.p>
        </motion.div>

        {/* Steps Timeline - Design amélioré */}
        <div className="ms-timeline">
          <div className="ms-steps-container">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                className={`ms-step-card ${activeStep === step.id ? 'active' : ''}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                onMouseEnter={() => setActiveStep(step.id)}
              >
                {/* Effet de bordure lumineuse au hover */}
                <div className="ms-step-card-border-glow" style={{ background: `linear-gradient(90deg, ${step.color}, ${step.color}80, transparent)` }} />
                
                {/* Overlay de gradient au survol */}
                <div 
                  className="ms-step-card-hover-overlay" 
                  style={{ background: `radial-gradient(circle at 30% 20%, ${step.color}15, transparent 80%)` }}
                />

                <div className="ms-step-connector">
                  <motion.div 
                    className="ms-step-number-wrapper"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="ms-step-number" style={{ background: step.gradient }}>
                      <span>{step.number}</span>
                    </div>
                  </motion.div>
                  {index < steps.length - 1 && <div className="ms-step-line" />}
                </div>

                <motion.div 
                  className="ms-step-content"
                  style={{
                    borderColor: activeStep === step.id ? step.color : 'rgba(59,130,246,0.15)',
                  }}
                >
                  {/* Icône d'arrière-plan discrète */}
                  <div className="ms-step-bg-icon">
                    {step.iconAlt}
                  </div>

                  <div className="ms-step-icon-wrapper" style={{ background: step.gradient }}>
                    {step.icon}
                    <div className="ms-step-icon-glow"></div>
                  </div>
                  
                  <h3 className="ms-step-title">{step.title}</h3>
                  <p className="ms-step-description">{step.description}</p>

                  {/* Stats Preview - Plus élégant avec texte blanc */}
                  {activeStep !== step.id && (
                    <div className="ms-step-stats-preview">
                      {step.stats.map((stat, idx) => (
                        <motion.div 
                          key={idx} 
                          className="ms-step-stat-pill"
                          whileHover={{ scale: 1.03 }}
                        >
                          <MdFlashOn style={{ color: step.color }} />
                          <span className="ms-step-stat-text">{stat}</span>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Details that expand on hover/active - Design raffiné */}
                  <AnimatePresence>
                    {activeStep === step.id && (
                      <motion.div 
                        className="ms-step-details"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                      >
                        <div className="ms-details-list">
                          {step.details.map((detail, idx) => (
                            <motion.div 
                              key={idx} 
                              className="ms-detail-item"
                              initial={{ opacity: 0, x: -15 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.05 }}
                            >
                              <MdCheckCircle className="ms-detail-icon" style={{ color: step.color }} />
                              <span>{detail}</span>
                            </motion.div>
                          ))}
                        </div>
                        
                        <div className="ms-step-meta">
                          <div className="ms-meta-item">
                            <MdTimer className="ms-meta-icon" style={{ color: step.color }} />
                            <span>{step.duration}</span>
                          </div>
                          <div className="ms-meta-item">
                            <FaGem className="ms-meta-icon" style={{ color: step.color }} />
                            <span>{step.price}</span>
                          </div>
                          <div className="ms-meta-item">
                            <MdPeople className="ms-meta-icon" style={{ color: step.color }} />
                            <span>{step.commitment}</span>
                          </div>
                        </div>

                        <motion.button 
                          className="ms-step-cta"
                          style={{ background: step.gradient }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <span>Explorer cette étape</span>
                          <MdArrowForward className="ms-step-cta-icon" />
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Minimal view indicator - Plus épuré */}
                  {activeStep !== step.id && (
                    <div className="ms-step-hint">
                      <div className="ms-step-hint-line"></div>
                      <span>Survolez pour découvrir</span>
                      <MdAutoAwesome className="ms-step-hint-icon" />
                    </div>
                  )}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section - Complètement isolée avec ses propres classes */}
        <motion.div 
          className="ms-benefits-section"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="ms-benefits-section-header">
            <div className="ms-benefits-section-badge">
              <FaCrown />
              <span>Pourquoi nous choisir</span>
            </div>
            <h3>Des avantages <span className="ms-benefits-section-gradient">concrets</span> pour votre entreprise</h3>
          </div>

          <div className="ms-benefits-section-grid">
            <motion.div className="ms-benefit-section-card" whileHover={{ y: -6 }}>
              <div className="ms-benefit-section-icon-wrapper">
                <MdFlashOn className="ms-benefit-section-icon" />
              </div>
              <h4>Rapide à mettre en place</h4>
              <p>Des résultats visibles dès la première semaine d'implémentation</p>
              <div className="ms-benefit-section-stats">
                <span className="ms-benefit-section-stat">-80% temps</span>
              </div>
            </motion.div>

            <motion.div className="ms-benefit-section-card" whileHover={{ y: -6 }}>
              <div className="ms-benefit-section-icon-wrapper">
                <MdWorkspacePremium className="ms-benefit-section-icon" />
              </div>
              <h4>100% sur mesure</h4>
              <p>Adapté à vos outils, votre équipe et votre façon de travailler</p>
              <div className="ms-benefit-section-stats">
                <span className="ms-benefit-section-stat">Personnalisation totale</span>
              </div>
            </motion.div>

            <motion.div className="ms-benefit-section-card" whileHover={{ y: -6 }}>
              <div className="ms-benefit-section-icon-wrapper">
                <MdVerified className="ms-benefit-section-icon" />
              </div>
              <h4>Sans risque</h4>
              <p>Diagnostic gratuit et accompagnement jusqu'à votre autonomie</p>
              <div className="ms-benefit-section-stats">
                <span className="ms-benefit-section-stat">100% satisfait</span>
              </div>
            </motion.div>

            <motion.div className="ms-benefit-section-card" whileHover={{ y: -6 }}>
              <div className="ms-benefit-section-icon-wrapper">
                <MdAnalytics className="ms-benefit-section-icon" />
              </div>
              <h4>ROI garanti</h4>
              <p>Un retour sur investissement mesurable dès les premières semaines</p>
              <div className="ms-benefit-section-stats">
                <span className="ms-benefit-section-stat">+300% ROI moyen</span>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Enhanced CTA Section - Design plus élégant */}
        <motion.div 
          className="ms-cta-wrapper"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="ms-cta-card">
            <div className="ms-cta-glow"></div>
            
            <div className="ms-cta-left">
              <div className="ms-cta-crown-wrapper">
                <FaCrown className="ms-cta-crown" />
              </div>
              <h3>Prêt à <span className="ms-cta-gradient">transformer</span> votre organisation ?</h3>
              <p>Diagnostic gratuit de 30 min pour identifier vos opportunités d'automatisation</p>
              
              <div className="ms-cta-features">
                <div className="ms-cta-feature">
                  <MdCheckCircle />
                  <span>Sans engagement</span>
                </div>
                <div className="ms-cta-feature">
                  <MdTimer />
                  <span>30 min chrono</span>
                </div>
                <div className="ms-cta-feature">
                  <FaBrain />
                  <span>Expert dédié</span>
                </div>
              </div>
            </div>
            
            <motion.button 
              className="ms-cta-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <span>Réserver mon diagnostic</span>
              <MdArrowForward className="ms-cta-button-icon" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MethodSection;